import React, { useState, useRef, useEffect } from "react";
import { confirm } from "../utils/confirm";
import RicordamiAlle from "./RicordamiAlle";
import { toLocalDateStr } from "../utils/dateUtils";
import { X, Trash2, CheckCircle } from "lucide-react";
import { doc, updateDoc, deleteDoc, getDoc, addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { isTaskOverdue } from "../utils/taskHelpers";
import { useToast } from "../utils/Toast";
import { useAuth } from "../contexts/AuthContext";
import { ruoloSuAttivita, puoGestireSottoattivita } from "../utils/condivisione";
import { avvisaCambioStatoSottoattivita } from "../utils/avvisiAttivita";
import DescrizioneAttivita from "./DescrizioneAttivita";
import CommentiAttivita from "./CommentiAttivita";
import { campiCompletamento, avvisoCompletamentoDiAltri, testoCompletataDa } from "../utils/completamentoAttivita";

export default function SubtaskDetailPanel({ subtask, onClose, onUpdated, onDeleted }) {
  const { addToast } = useToast();
  const [panelWidth, setPanelWidth] = useState(380);
  const resizingRef = useRef(false);
  const { currentUser, userProfile, isOrgAdmin, isPlatformOwner } = useAuth();
  // Diritti sull'attività madre (utils/condivisione.js): chi collabora modifica ed elimina
  // solo le sottoattività che ha creato o che ha in carico, e solo quelle può completare
  // (ottobre 2026: stesse regole ovunque, utils/completamentoAttivita.js).
  const [ruoloMadre, setRuoloMadre] = useState(null);
  const [projectIdMadre, setProjectIdMadre] = useState(null);
  const [commessaChiusa, setCommessaChiusa] = useState(false);
  useEffect(() => {
    if (!subtask?.parentTaskId) { setRuoloMadre("pieno"); return; }
    let vivo = true;
    (async () => {
      try {
        const t = await getDoc(doc(db, "tasks", subtask.parentTaskId));
        const task = t.exists() ? { id: t.id, ...t.data() } : null;
        if (vivo) setProjectIdMadre(task?.projectId || null);
        const p = task?.projectId ? await getDoc(doc(db, "projects", task.projectId)) : null;
        const pd = p?.exists() ? p.data() : {};
        if (vivo) setCommessaChiusa(pd.statoCommessa === "chiusa");
        const isAdmin = !!(isOrgAdmin || isPlatformOwner || ["admin", "titolare"].includes(userProfile?.role));
        if (vivo) setRuoloMadre(task ? ruoloSuAttivita(task, { uid: currentUser?.uid, isAdmin, ruoloOperativo: userProfile?.ruoloOperativo || "",
          responsabileCommessaUid: pd.responsabileUid || "", membriCommessa: pd.membri || [] }) : "pieno");
      } catch { if (vivo) setRuoloMadre("collabora"); }
    })();
    return () => { vivo = false; };
  }, [subtask?.parentTaskId, currentUser?.uid]);

  if (!subtask) return null;
  const puoModificare = ruoloMadre !== null && puoGestireSottoattivita(subtask, ruoloMadre, currentUser?.uid);

  const update = async (field, value) => {
    if (!puoModificare) return; // lo stato passa da toggleComplete
    await updateDoc(doc(db, "subtasks", subtask.id), { [field]: value });
    if (onUpdated) onUpdated({ ...subtask, [field]: value });
    // Scadenza portata a OGGI da un altro: avviso subito all'assegnatario (il controllo delle 07:00 è già passato)
    if (field === "dueDate" && value && value !== subtask.dueDate && value === toLocalDateStr(new Date()) &&
        subtask.assignedTo && subtask.assignedTo !== currentUser?.uid && subtask.organizationId) {
      addDoc(collection(db, "notifiche"), {
        userId: subtask.assignedTo, orgId: subtask.organizationId, tipo: "scadenza_oggi", titolo: "Scadenza anticipata a oggi",
        testo: `${currentUser?.displayName || "Un collega"} ha portato a oggi la scadenza di «${subtask.title || "una sottoattività"}»`,
        projectId: subtask.projectId || null, taskId: subtask.parentTaskId || null, rifCollezione: "subtasks", rifId: subtask.id,
        letta: false, createdAt: serverTimestamp(),
      }).catch(err => console.warn("Avviso scadenza non inviato:", err?.code || err));
    }
  };

  const toggleComplete = async () => {
    if (ruoloMadre === null) return;
    if (!puoModificare) {
      addToast(`Questa sottoattività la può completare chi l'ha creata${subtask.assignedName ? `, ${subtask.assignedName}` : " o chi ne è incaricato"} o chi gestisce l'attività`, "viola", 7000);
      return;
    }
    const newStatus = subtask.status === "completata" ? "aperta" : "completata";
    try {
      await updateDoc(doc(db, "subtasks", subtask.id), { status: newStatus, ...campiCompletamento(newStatus, currentUser), updatedAt: serverTimestamp() });
      if (onUpdated) onUpdated({ ...subtask, status: newStatus });
      avvisaCambioStatoSottoattivita(subtask, newStatus, currentUser, { projectId: projectIdMadre });
      const viola = newStatus === "completata" ? avvisoCompletamentoDiAltri(subtask, currentUser?.uid, true) : null;
      if (viola) addToast(viola, "viola", 8000);
      else addToast(newStatus === "completata" ? "Sottoattività completata!" : "Sottoattività riaperta", "success");
    } catch (err) { addToast("Sottoattività non aggiornata: " + (err?.message || err), "error"); }
  };

  const del = async () => {
    if (!puoModificare) return;
    if (!await confirm(`Eliminare la sottoattività "${subtask.title}"?`, { type:"danger" })) return;
    await deleteDoc(doc(db, "subtasks", subtask.id));
    addToast("Sottoattività eliminata", "info");
    if (onDeleted) onDeleted();
    onClose();
  };

  // Ridimensionamento - solo restringere, max 50% finestra
  const startResize = e => {
    e.preventDefault();
    resizingRef.current = true;
    const startX = e.clientX;
    const startWidth = panelWidth;
    const maxWidth = window.innerWidth / 2;

    const onMove = me => {
      if (!resizingRef.current) return;
      const delta = startX - me.clientX;
      let newWidth = startWidth + delta;
      if (newWidth > maxWidth) newWidth = maxWidth;
      if (newWidth < 280) newWidth = 280;
      setPanelWidth(newWidth);
    };
    const onUp = () => {
      resizingRef.current = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  return (
    <div className="detail-panel" style={{ width: panelWidth, position:"relative", flexShrink:0, borderLeft:"1px solid #e5e7eb", boxShadow:"-4px 0 16px rgba(0,0,0,0.06)", zIndex:30 }}>
      {/* Maniglia ridimensionamento */}
      <div className="detail-panel-maniglia" onMouseDown={startResize}
        style={{ position:"absolute", left:0, top:0, bottom:0, width:6, cursor:"col-resize", zIndex:50 }}
        onMouseOver={e=>e.currentTarget.style.background="rgba(124,58,237,0.2)"}
        onMouseOut={e=>e.currentTarget.style.background="transparent"}/>

      {/* Header */}
      <div className="detail-panel-header">
        <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
          <div onClick={toggleComplete} title={subtask.status==="completata"?"Riapri sottoattività":"Completa sottoattività"}
            className={`check-circle ${subtask.status==="completata"?"checked":""}`}
            style={{width:20,height:20,marginTop:2,flexShrink:0}}>
            {subtask.status==="completata" && (
              <svg width={12} height={12} viewBox="0 0 10 10"><polyline points="1,5 4,8 9,2" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round"/></svg>
            )}
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:11,fontWeight:600,color:"#9ca3af",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>
              Sottoattività
            </div>
            <div contentEditable={puoModificare} suppressContentEditableWarning
              onBlur={e=>{ const v=e.currentTarget.textContent.trim(); if(v && v!==subtask.title) update("title", v); }}
              style={{fontSize:16,fontWeight:600,lineHeight:1.4,outline:"none",minHeight:24,textDecoration:subtask.status==="completata"?"line-through":"none",color:subtask.status==="completata"?"#9ca3af":"#111827"}}>
              {subtask.title}
            </div>
          </div>
          <div style={{display:"flex",gap:4,marginRight:40}}>
            {puoModificare && <button className="btn-icon" onClick={del} title="Elimina"><Trash2 size={15}/></button>}
            <button className="btn-icon" onClick={onClose} aria-label="Chiudi" title="Chiudi"><X size={16}/></button>
          </div>
        </div>
        {/* Pulsante completa grande */}
        <button onClick={toggleComplete}
          style={{marginTop:12,width:"100%",padding:"8px",border:`1px solid ${subtask.status==="completata"?"#059669":"#e5e7eb"}`,borderRadius:8,background:subtask.status==="completata"?"#d1fae5":"#fff",color:subtask.status==="completata"?"#059669":"#374151",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:6,transition:"all 0.15s"}}>
          <CheckCircle size={15}/> {subtask.status==="completata" ? "Completata — clicca per riaprire" : "Segna come completata"}
        </button>
        {testoCompletataDa(subtask) && <div style={{fontSize:11.5,color:"#059669",fontWeight:600,marginTop:6,textAlign:"center"}}>✓ {testoCompletataDa(subtask)}</div>}
      </div>

      <div className="detail-panel-scroll">
        {ruoloMadre !== null && !puoModificare && (
          <div style={{fontSize:11.5,color:"#6b7280",background:"#f9fafb",borderRadius:8,padding:"6px 10px",margin:"0 0 10px"}}>
            👥 Puoi spuntarla; la modifica chi l'ha creata, chi l'ha in carico o chi segue l'attività.
          </div>
        )}
        <fieldset disabled={!puoModificare} style={{border:"none",margin:0,padding:0,minWidth:0}}>
        {/* Info rapide */}
        <div className="detail-section" style={{display:"flex",gap:16,flexWrap:"wrap"}}>
            <div>
              <div className="detail-section-title">Data inizio</div>
              <input type="date" defaultValue={subtask.startDate||""} onBlur={e=>update("startDate",e.target.value)}
                style={{border:subtask.startDate?"none":"1px solid #e5e7eb",borderRadius:6,background:"transparent",fontSize:subtask.startDate?14:13,color:subtask.startDate?"#111827":"#9ca3af",cursor:"pointer",fontFamily:"'Inter',sans-serif",outline:"none",padding:subtask.startDate?0:"4px 8px"}}/>
              <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>facoltativa — usata solo per il Gantt</div>
            </div>
            {subtask.dueDate && (
              <div>
                <div className="detail-section-title">Scadenza</div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <input type="date" defaultValue={subtask.dueDate} onBlur={e=>update("dueDate",e.target.value)}
                    style={{border:"none",background:"transparent",fontSize:14,color:isTaskOverdue(subtask)?"#dc2626":"#111827",cursor:"pointer",fontFamily:"'Inter',sans-serif",outline:"none",padding:0}}/>
                  <input type="time" defaultValue={subtask.dueTime||""} onBlur={e=>update("dueTime",e.target.value)}
                    style={{border:"1px solid #e5e7eb",borderRadius:6,fontSize:12,color:subtask.dueTime?"#374151":"#9ca3af",cursor:"pointer",fontFamily:"'Inter',sans-serif",outline:"none",padding:"2px 6px"}}/>
                  {isTaskOverdue(subtask) && <span style={{fontSize:10,color:"#dc2626",fontWeight:600,background:"#fee2e2",padding:"1px 5px",borderRadius:4}}>SCADUTA</span>}
                </div>
              </div>
            )}
            {!subtask.dueDate && (
              <div>
                <div className="detail-section-title">Scadenza</div>
                <input type="date" onBlur={e=>update("dueDate",e.target.value)}
                  style={{border:"1px solid #e5e7eb",borderRadius:6,fontSize:13,color:"#9ca3af",cursor:"pointer",fontFamily:"'Inter',sans-serif",outline:"none",padding:"4px 8px"}}/>
              </div>
            )}
            {subtask.assignedName && (
              <div>
                <div className="detail-section-title">Assegnato a</div>
                <div style={{fontSize:14,color:"#111827"}}>👤 {subtask.assignedName}</div>
              </div>
            )}
            {subtask.priority && (
              <div>
                <div className="detail-section-title">Priorità</div>
                <span className={`badge badge-${subtask.priority}`}>{subtask.priority}</span>
              </div>
            )}
          </div>

        {/* Promemoria */}
        <div className="detail-section">
          <div className="detail-section-title">Avviso giorni prima della scadenza</div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
            <input type="checkbox" checked={!!subtask.reminderEnabled} onChange={e=>update("reminderEnabled",e.target.checked)} style={{accentColor:"#7c3aed"}}/>
            {subtask.reminderEnabled && <>
              <input type="number" defaultValue={subtask.reminderDays||1} onBlur={e=>update("reminderDays",parseInt(e.target.value)||1)}
                min={1} max={365} style={{width:50,border:"1px solid #e5e7eb",borderRadius:4,padding:"2px 6px",fontSize:12,fontFamily:"'Inter',sans-serif"}}/>
              <span style={{fontSize:12,color:"#6b7280"}}>giorni prima</span>
            </>}
          </div>
          <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13}}>
            <input type="checkbox" checked={subtask.emailOnReminder !== false} onChange={e=>update("emailOnReminder",e.target.checked)} style={{accentColor:"#7c3aed"}}/>
            📧 Invia mail al promemoria
          </label>
        </div>

        {/* Promemoria a orario scelto (personale: si può impostare anche senza poter modificare la sottoattività) */}
        </fieldset>
        <div className="detail-section">
          <div className="detail-section-title">🔔 Ricordamelo</div>
          <RicordamiAlle collezione="subtasks" item={subtask} projectId={subtask.projectId || null} taskIdDaAprire={subtask.parentTaskId || null} />
        </div>
        <fieldset disabled={!puoModificare} style={{border:"none",margin:0,padding:0,minWidth:0}}>

        {/* Email scadenza/scaduta */}
        <div className="detail-section" style={{display:"flex",gap:16}}>
          <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13}}>
            <input type="checkbox" checked={subtask.emailOnDue !== false} onChange={e=>update("emailOnDue",e.target.checked)} style={{accentColor:"#7c3aed"}}/>
            📧 Mail alla scadenza
          </label>
          <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13}}>
            <input type="checkbox" checked={subtask.emailOnOverdue !== false} onChange={e=>update("emailOnOverdue",e.target.checked)} style={{accentColor:"#7c3aed"}}/>
            📧 Mail se scaduta
          </label>
        </div>

        {/* Descrizione - grande, nera, modificabile */}
        <div className="detail-section">
          <div className="detail-section-title">Descrizione</div>
          <DescrizioneAttivita key={subtask.id} valore={subtask.description || ""} onSalva={v => update("description", v)}
            disabilitato={!puoModificare} placeholder="Aggiungi una descrizione per questa sottoattività..." minRighe={8}/>
        </div>
        </fieldset>

        {/* Commenti con foto e file (W440): legati a questa sottoattività */}
        {subtask.parentTaskId && (
          <CommentiAttivita key={subtask.id} taskId={subtask.parentTaskId} subtaskId={subtask.id} task={subtask}
            orgId={subtask.organizationId} projectId={projectIdMadre || subtask.projectId || null} commessaChiusa={commessaChiusa}/>
        )}
      </div>
    </div>
  );
}
